const FEMALE_VOICE_HINTS = [
  "female",
  "femme",
  "woman",
  "audrey",
  "aurelie",
  "amelie",
  "lea",
  "julie",
  "hortense",
  "denise",
  "celine",
  "celeste",
  "marie",
  "alice",
  "charlotte",
  "virginie",
];

const MALE_VOICE_HINTS = [
  "male",
  "homme",
  "man",
  "thomas",
  "henri",
  "jacques",
  "nicolas",
  "paul",
  "daniel",
];

const LIA_PARIS_VOICE_HINTS = [
  "audrey",
  "aurelie",
  "amelie",
  "lea",
  "julie",
  "hortense",
  "denise",
  "celine",
  "celeste",
  "marie",
  "alice",
  "charlotte",
  "google francais",
];

function normalizeVoiceLabel(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function voiceScore(voice, preferredHints = []) {
  const lang = normalizeVoiceLabel(voice?.lang).replace("_", "-");
  const label = normalizeVoiceLabel(`${voice?.name || ""} ${voice?.voiceURI || ""}`);
  // Keep a France voice ahead of other French locales so LIA does not switch
  // to a Canadian or Belgian accent just because its voice has a familiar name.
  let score = lang === "fr-fr" ? 200 : 40;

  const preferredIndex = preferredHints.findIndex((hint) => label.includes(hint));
  if (preferredIndex >= 0) score += 80 - preferredIndex;
  if (FEMALE_VOICE_HINTS.some((hint) => label.includes(hint))) score += 35;
  if (MALE_VOICE_HINTS.some((hint) => label.includes(hint))) score -= 120;
  if (/natural|neural|premium|enhanced/.test(label)) score += 6;

  return score;
}

function pickFrenchVoice(voices, preferredHints = FEMALE_VOICE_HINTS) {
  const list = Array.isArray(voices) ? voices : [];
  if (!list.length) return null;
  const fr = list.filter((v) => String(v?.lang || "").toLowerCase().startsWith("fr"));
  if (!fr.length) return null;
  return [...fr].sort(
    (left, right) =>
      voiceScore(right, preferredHints) - voiceScore(left, preferredHints)
  )[0];
}

function speakFrenchVoice(text, opts = {}) {
  const content = String(text || "").trim();
  if (!content || typeof window === "undefined" || !window.speechSynthesis) return;
  if (!isAudioNarrationEnabled()) return;
  try {
    const synth = window.speechSynthesis;
    let hasSpoken = false;
    let fallbackTimer = null;

    const cleanupVoiceListener = () => {
      synth.removeEventListener("voiceschanged", onVoicesChanged);
      if (fallbackTimer !== null) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
    };

    const say = () => {
      if (hasSpoken) return;
      hasSpoken = true;
      const utter = new SpeechSynthesisUtterance(content);
      utter.lang = opts.lang || "fr-FR";
      utter.rate = Number(opts.rate) || 1;
      utter.pitch = Number(opts.pitch) || 1;
      utter.volume = Number.isFinite(opts.volume) ? opts.volume : 1;
      const voice = pickFrenchVoice(synth.getVoices(), opts.preferredVoiceHints);
      if (voice) utter.voice = voice;
      utter.addEventListener("end", () => {
        if (typeof opts.onEnd === "function") opts.onEnd();
      });
      if (typeof opts.onStart === "function") opts.onStart();
      synth.cancel();
      synth.speak(utter);
    };

    const onVoicesChanged = () => {
      cleanupVoiceListener();
      say();
    };

    const voices = synth.getVoices();
    if (voices && voices.length) {
      say();
      return;
    }

    synth.addEventListener("voiceschanged", onVoicesChanged);
    fallbackTimer = window.setTimeout(() => {
      cleanupVoiceListener();
      say();
    }, 450);
  } catch (_) {
    // no-op
  }
}

export function speakRewardsParisVoice(text, opts = {}) {
  speakFrenchVoice(text, {
    lang: "fr-FR",
    rate: 1,
    pitch: 1.08,
    volume: 1,
    ...opts,
  });
}

export function speakLiaLoginVoice(text, opts = {}) {
  speakFrenchVoice(text, {
    lang: "fr-FR",
    rate: 1.04,
    pitch: 1.14,
    volume: 1,
    preferredVoiceHints: LIA_PARIS_VOICE_HINTS,
    ...opts,
  });
}

const AUDIO_TOGGLE_KEY = "eternua.audioNarrationEnabled";

export function isAudioNarrationEnabled() {
  if (typeof window === "undefined") return true;
  const stored = window.localStorage.getItem(AUDIO_TOGGLE_KEY);
  if (stored === null) return true;
  return stored !== "false";
}

export function setAudioNarrationEnabled(value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(AUDIO_TOGGLE_KEY, value ? "true" : "false");
  } catch (_) {
    // no-op
  }
}
